import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { X, MapPin, Loader2, Check, Upload, ImageIcon, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import PiecesJointes from './PiecesJointes';
import { searchAddress, type NominatimSuggestion } from '../utils/geo';
import { supabase } from '@/integrations/supabase/client';
import { useCrm, crmStore } from '../store';
import { TvaSelect } from './TvaSelect';

export interface ArtisteRow {
  id?: string;
  nom_scene: string;
  nom?: string | null;
  prenom?: string | null;
  civilite?: string | null;
  statut?: string | null;
  domaine_principal?: string | null;
  domaines?: string[] | null;
  segment?: string | null;
  segments?: string[] | null;
  tags?: string[] | null;
  email?: string | null;
  email_admin?: string | null;
  tel?: string | null;
  tel_secondaire?: string | null;
  adresse?: string | null;
  cp?: string | null;
  ville?: string | null;
  departement?: string | null;
  pays?: string | null;
  site_web?: string | null;
  instagram?: string | null;
  facebook?: string | null;
  tiktok?: string | null;
  youtube?: string | null;
  linkedin?: string | null;
  photo_url?: string | null;
  siret?: string | null;
  statut_juridique?: string | null;
  iban?: string | null;
  bic?: string | null;
  tarif_base?: number | null;
  frais_deplacement?: number | null;
  tva_pct?: number | null;
  zone_intervention?: string | null;
  duree_montage?: string | null;
  contraintes_techniques?: string | null;
  conditions_particulieres?: string | null;
  niveau_confiance?: number | null;
  remarque_interne?: string | null;
  lat?: number | null;
  lng?: number | null;
  geocoded_at?: string | null;
}

const empty: ArtisteRow = {
  nom_scene: '', statut: 'solo', pays: 'France', tva_pct: 20, niveau_confiance: 3,
  domaines: [], segments: [], tags: [],
};

const SEGMENTS_SUGGESTIONS = [
  'Mariage', 'Corporate', 'Anniversaire', 'Baptême', 'Cocktail', 'Soirée privée',
  'Festival', 'Fête de village', 'Inauguration', 'Séminaire', 'Team building',
  'Arbre de Noël', 'Cérémonie laïque', 'Salon professionnel',
];

const TAGS_SUGGESTIONS = [
  'Coup de cœur', 'Fiable', 'Polyvalent', 'Premium', 'Économique', 'Disponible week-end',
  'Itinérant', 'Bilingue', 'Vegan', 'Sans alcool', 'Adapté enfants', 'Show déambulatoire',
  'Sonorisation incluse', 'Tenue de gala',
];

// Classes communes pour inputs/textareas/selects sur fond sombre — assure la lisibilité
const inputCls =
  'w-full px-3 py-2 bg-slate-900/60 border border-slate-800 rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-amber-500/60 focus:bg-slate-900';

interface Props {
  open: boolean;
  initial?: ArtisteRow | null;
  onClose: () => void;
  onSave: (data: ArtisteRow) => Promise<void>;
}

export default function ArtisteForm({ open, initial, onClose, onSave }: Props) {
  const crm = useCrm();
  const [form, setForm] = useState<ArtisteRow>(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setForm(initial ? { ...empty, ...initial } : empty); }, [initial, open]);

  const set = (k: keyof ArtisteRow, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const handleUploadPhoto = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Le fichier doit être une image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop lourde (max 5 Mo)');
      return;
    }
    setUploading(true);
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error } = await supabase.storage.from('artistes-photos').upload(path, file, {
        cacheControl: '3600',
        upsert: false,
      });
      if (error) throw error;
      const { data } = supabase.storage.from('artistes-photos').getPublicUrl(path);
      set('photo_url', data.publicUrl);
      toast.success('Photo uploadée');
    } catch (e: any) {
      console.error(e);
      toast.error(`Erreur upload : ${e?.message ?? 'inconnue'}`);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.nom_scene.trim()) return;
    setSaving(true);
    try { await onSave(form); onClose(); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-950 border-slate-800 text-slate-100 max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-amber-400">
            {initial?.id ? 'Modifier l’artiste' : 'Nouvel artiste'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Identité */}
          <section>
            <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Identité</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Nom de scène *">
                <input className={inputCls} value={form.nom_scene} onChange={(e) => set('nom_scene', e.target.value)} />
              </Field>
              <Field label="Civilité">
                <select className={inputCls} value={form.civilite || ''} onChange={(e) => set('civilite', e.target.value)}>
                  <option value="" className="bg-slate-950">—</option>
                  <option value="M." className="bg-slate-950">M.</option>
                  <option value="Mme" className="bg-slate-950">Mme</option>
                  <option value="Autre" className="bg-slate-950">Autre</option>
                </select>
              </Field>
              <Field label="Statut">
                <select className={inputCls} value={form.statut || 'solo'} onChange={(e) => set('statut', e.target.value)}>
                  <option value="solo" className="bg-slate-950">Solo</option>
                  <option value="duo" className="bg-slate-950">Duo</option>
                  <option value="trio" className="bg-slate-950">Trio</option>
                  <option value="groupe" className="bg-slate-950">Groupe</option>
                  <option value="collectif" className="bg-slate-950">Collectif</option>
                </select>
              </Field>
              <Field label="Prénom (référent)"><input className={inputCls} value={form.prenom || ''} onChange={(e) => set('prenom', e.target.value)} /></Field>
              <Field label="Nom (référent)"><input className={inputCls} value={form.nom || ''} onChange={(e) => set('nom', e.target.value)} /></Field>
              <div className="md:col-span-3">
                <Field label="Domaines (sélection multiple)">
                  <MultiPicker
                    values={form.domaines || []}
                    suggestions={crm.domaines || []}
                    placeholder="Choisir ou saisir un domaine (DJ, Magicien…)"
                    onChange={(arr) => setForm((f) => ({ ...f, domaines: arr, domaine_principal: arr[0] || null }))}
                    onCreateSuggestion={(v) => {
                      const exists = (crm.domaines || []).some((d) => d.toLowerCase() === v.toLowerCase());
                      if (!exists) {
                        crmStore.update({ domaines: [...(crm.domaines || []), v].sort() } as any);
                        toast.success(`« ${v} » ajouté aux domaines (Paramètres)`);
                      }
                    }}
                  />
                </Field>
              </div>
              <div className="md:col-span-3">
                <Field label="Segments (sélection multiple)">
                  <MultiPicker
                    values={form.segments || []}
                    suggestions={SEGMENTS_SUGGESTIONS}
                    placeholder="Mariage, Corporate, Festival…"
                    onChange={(arr) => setForm((f) => ({ ...f, segments: arr, segment: arr[0] || null }))}
                  />
                </Field>
              </div>
              <Field label="Niveau de confiance (1-5)">
                <input type="number" min={1} max={5} className={inputCls} value={form.niveau_confiance ?? 3} onChange={(e) => set('niveau_confiance', Number(e.target.value))} />
              </Field>
            </div>
          </section>

          {/* Photo */}
          <section>
            <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Photo</h3>
            <div className="border border-slate-800 bg-slate-900/40 rounded-lg p-4">
              <div className="flex items-start gap-4 flex-wrap">
                <div className="w-24 h-24 shrink-0 rounded-lg border border-slate-800 bg-slate-950/60 overflow-hidden flex items-center justify-center">
                  {form.photo_url ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={form.photo_url} alt="Aperçu" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                  ) : (
                    <ImageIcon size={28} className="text-slate-600" strokeWidth={1.5} />
                  )}
                </div>
                <div className="flex-1 min-w-[260px] space-y-2">
                  <Field label="URL de la photo">
                    <input className={inputCls} value={form.photo_url || ''} onChange={(e) => set('photo_url', e.target.value)} placeholder="https://…" />
                  </Field>
                  <div className="flex gap-2 flex-wrap">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      hidden
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleUploadPhoto(f);
                        e.target.value = '';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="flex items-center gap-2 px-3 py-2 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 text-amber-300 rounded-lg text-xs disabled:opacity-50"
                    >
                      {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} strokeWidth={1.5} />}
                      {uploading ? 'Upload…' : 'Uploader un fichier (PNG, JPG…)'}
                    </button>
                    {form.photo_url && (
                      <button
                        type="button"
                        onClick={() => set('photo_url', '')}
                        className="flex items-center gap-2 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-300 rounded-lg text-xs"
                      >
                        <Trash2 size={13} strokeWidth={1.5} /> Retirer
                      </button>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-500">JPG, PNG ou WebP — max 5 Mo. L'URL et l'upload sont équivalents.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section>
            <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Contact</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Email principal"><input type="email" className={inputCls} value={form.email || ''} onChange={(e) => set('email', e.target.value)} /></Field>
              <Field label="Email administratif"><input type="email" className={inputCls} value={form.email_admin || ''} onChange={(e) => set('email_admin', e.target.value)} /></Field>
              <Field label="Téléphone"><input className={inputCls} value={form.tel || ''} onChange={(e) => set('tel', e.target.value)} /></Field>
              <Field label="Téléphone secondaire"><input className={inputCls} value={form.tel_secondaire || ''} onChange={(e) => set('tel_secondaire', e.target.value)} /></Field>
            </div>
          </section>

          {/* Adresse */}
          <section>
            <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Adresse</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-3">
                <Field label="Adresse (autocomplete + géocodage)">
                  <AdresseAutocomplete
                    value={form.adresse || ''}
                    geocoded={!!(form.lat && form.lng)}
                    onChange={(v) => set('adresse', v)}
                    onPick={(s) => {
                      const addr = s.display_name;
                      const parts: any = (s as any).address || {};
                      setForm((f) => ({
                        ...f,
                        adresse: addr,
                        cp: parts.postcode || f.cp,
                        ville: parts.city || parts.town || parts.village || parts.municipality || f.ville,
                        departement: parts.county || f.departement,
                        pays: parts.country || f.pays,
                        lat: parseFloat(s.lat),
                        lng: parseFloat(s.lon),
                        geocoded_at: new Date().toISOString(),
                      }));
                      toast.success('Adresse géocodée');
                    }}
                  />
                </Field>
              </div>
              <Field label="CP"><input className={inputCls} value={form.cp || ''} onChange={(e) => set('cp', e.target.value)} /></Field>
              <Field label="Ville"><input className={inputCls} value={form.ville || ''} onChange={(e) => set('ville', e.target.value)} /></Field>
              <Field label="Département"><input className={inputCls} value={form.departement || ''} onChange={(e) => set('departement', e.target.value)} /></Field>
              <Field label="Pays"><input className={inputCls} value={form.pays || 'France'} onChange={(e) => set('pays', e.target.value)} /></Field>
              <Field label="Zone d'intervention"><input className={inputCls} value={form.zone_intervention || ''} onChange={(e) => set('zone_intervention', e.target.value)} placeholder="100 km autour de Troyes" /></Field>
            </div>
            {form.lat && form.lng && (
              <div className="mt-2 text-[10px] text-emerald-400/80 flex items-center gap-1.5">
                <Check size={12} strokeWidth={2} /> Coordonnées GPS enregistrées ({Number(form.lat).toFixed(4)}, {Number(form.lng).toFixed(4)})
              </div>
            )}
          </section>

          {/* Réseaux */}
          <section>
            <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Réseaux & web</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Site web"><input className={inputCls} value={form.site_web || ''} onChange={(e) => set('site_web', e.target.value)} /></Field>
              <Field label="Instagram"><input className={inputCls} value={form.instagram || ''} onChange={(e) => set('instagram', e.target.value)} /></Field>
              <Field label="Facebook"><input className={inputCls} value={form.facebook || ''} onChange={(e) => set('facebook', e.target.value)} /></Field>
              <Field label="TikTok"><input className={inputCls} value={form.tiktok || ''} onChange={(e) => set('tiktok', e.target.value)} /></Field>
              <Field label="YouTube"><input className={inputCls} value={form.youtube || ''} onChange={(e) => set('youtube', e.target.value)} /></Field>
              <Field label="LinkedIn"><input className={inputCls} value={form.linkedin || ''} onChange={(e) => set('linkedin', e.target.value)} /></Field>
            </div>
          </section>

          {/* Administratif & financier */}
          <section>
            <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Administratif & financier</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Statut juridique"><input className={inputCls} value={form.statut_juridique || ''} onChange={(e) => set('statut_juridique', e.target.value)} placeholder="Auto-entrepreneur, SASU…" /></Field>
              <Field label="SIRET"><input className={inputCls} value={form.siret || ''} onChange={(e) => set('siret', e.target.value)} /></Field>
              <Field label="TVA %"><TvaSelect value={form.tva_pct ?? 20} onChange={(v) => set('tva_pct', v)} className={inputCls} /></Field>
              <Field label="Tarif de base (HT)"><input type="number" className={inputCls} value={form.tarif_base ?? ''} onChange={(e) => set('tarif_base', e.target.value === '' ? null : Number(e.target.value))} /></Field>
              <Field label="Frais de déplacement"><input type="number" className={inputCls} value={form.frais_deplacement ?? ''} onChange={(e) => set('frais_deplacement', e.target.value === '' ? null : Number(e.target.value))} /></Field>
              <Field label="IBAN"><input className={inputCls} value={form.iban || ''} onChange={(e) => set('iban', e.target.value)} /></Field>
              <Field label="BIC"><input className={inputCls} value={form.bic || ''} onChange={(e) => set('bic', e.target.value)} /></Field>
            </div>
          </section>

          {/* Technique */}
          <section>
            <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Technique & conditions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Durée de montage"><input className={inputCls} value={form.duree_montage || ''} onChange={(e) => set('duree_montage', e.target.value)} placeholder="1h30" /></Field>
              <div className="md:col-span-2"><Field label="Contraintes techniques"><textarea rows={2} className={inputCls} value={form.contraintes_techniques || ''} onChange={(e) => set('contraintes_techniques', e.target.value)} /></Field></div>
              <div className="md:col-span-2"><Field label="Conditions particulières"><textarea rows={2} className={inputCls} value={form.conditions_particulieres || ''} onChange={(e) => set('conditions_particulieres', e.target.value)} /></Field></div>
              <div className="md:col-span-2"><Field label="Remarque interne"><textarea rows={2} className={inputCls} value={form.remarque_interne || ''} onChange={(e) => set('remarque_interne', e.target.value)} /></Field></div>
            </div>
          </section>

          {/* Tags */}
          <section>
            <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Tags (sélection multiple)</h3>
            <MultiPicker
              values={form.tags || []}
              suggestions={TAGS_SUGGESTIONS}
              placeholder="Choisir ou saisir un tag (Coup de cœur, Premium…)"
              onChange={(arr) => set('tags', arr)}
            />
          </section>
        </div>

        {initial?.id && <PiecesJointes entityType="artiste" entityId={initial.id} />}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSave} disabled={saving || !form.nom_scene.trim()} className="bg-amber-500 hover:bg-amber-400 text-slate-950">
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-widest text-slate-400">{label}</Label>
      {children}
    </div>
  );
}

function AdresseAutocomplete({
  value,
  geocoded,
  onChange,
  onPick,
}: {
  value: string;
  geocoded: boolean;
  onChange: (v: string) => void;
  onPick: (s: NominatimSuggestion) => void;
}) {
  const [suggestions, setSuggestions] = useState<NominatimSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChange = (v: string) => {
    onChange(v);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (v.trim().length < 3) { setSuggestions([]); return; }
    debounceRef.current = window.setTimeout(async () => {
      setLoading(true);
      try {
        const data = await searchAddress(v);
        setSuggestions(data);
        setOpen(true);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 350);
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <input
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => suggestions.length && setOpen(true)}
          placeholder="Numéro, rue, ville…"
          autoComplete="off"
          className={inputCls + ' pr-9'}
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2">
          {loading ? (
            <Loader2 size={14} className="text-amber-400 animate-spin" />
          ) : geocoded ? (
            <Check size={14} className="text-emerald-400" strokeWidth={2} />
          ) : (
            <MapPin size={14} className="text-slate-500" strokeWidth={1.5} />
          )}
        </div>
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-slate-950 border border-slate-800 rounded-lg shadow-xl">
          {suggestions.map((s) => (
            <button
              key={s.place_id}
              type="button"
              onClick={() => { setOpen(false); setSuggestions([]); onPick(s); }}
              className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-slate-800/80 border-b border-slate-800/50 last:border-0 flex items-start gap-2"
            >
              <MapPin size={12} className="text-amber-400 mt-1 shrink-0" strokeWidth={1.5} />
              <span className="line-clamp-2">{s.display_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MultiPicker({
  values,
  suggestions,
  placeholder,
  onChange,
  onCreateSuggestion,
}: {
  values: string[];
  suggestions: string[];
  placeholder?: string;
  onChange: (v: string[]) => void;
  onCreateSuggestion?: (v: string) => void;
}) {
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const add = (v: string, isNew = false) => {
    const t = v.trim();
    if (!t) return;
    if (isNew && onCreateSuggestion) onCreateSuggestion(t);
    if (values.includes(t)) { setInput(''); return; }
    onChange([...values, t]);
    setInput('');
  };
  const remove = (v: string) => onChange(values.filter((x) => x !== v));

  const filtered = suggestions
    .filter((s) => !values.includes(s))
    .filter((s) => !input.trim() || s.toLowerCase().includes(input.toLowerCase()))
    .slice(0, 30);

  return (
    <div ref={wrapRef} className="relative">
      <div className="flex flex-wrap gap-1.5 p-2 bg-slate-900/60 border border-slate-800 rounded-lg min-h-[44px]">
        {values.map((v) => (
          <Badge key={v} variant="outline" className="border-amber-500/30 text-amber-400 bg-amber-500/10 gap-1">
            {v}
            <button type="button" onClick={() => remove(v)} className="ml-0.5 hover:text-amber-200">
              <X size={11} />
            </button>
          </Badge>
        ))}
        <input
          value={input}
          onChange={(e) => { setInput(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              const t = input.trim();
              const isNew = !!t && !suggestions.some((s) => s.toLowerCase() === t.toLowerCase());
              add(input, isNew);
            }
            else if (e.key === 'Backspace' && !input && values.length) { remove(values[values.length - 1]); }
          }}
          placeholder={values.length ? '' : placeholder || 'Ajouter…'}
          className="flex-1 min-w-[120px] bg-transparent outline-none text-sm text-slate-100 placeholder-slate-500"
        />
      </div>
      {open && (filtered.length > 0 || input.trim()) && (
        <div className="absolute z-50 left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-slate-950 border border-slate-800 rounded-lg shadow-xl">
          {filtered.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => { add(s); }}
              className="w-full text-left px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-800/80 border-b border-slate-800/50 last:border-0"
            >
              {s}
            </button>
          ))}
          {input.trim() && !suggestions.some((s) => s.toLowerCase() === input.trim().toLowerCase()) && (
            <button
              type="button"
              onClick={() => add(input, true)}
              className="w-full flex items-center gap-2 text-left px-3 py-1.5 text-sm text-amber-400 hover:bg-slate-800/80 border-t border-slate-800"
            >
              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-amber-500/60 text-amber-400">+</span>
              Ajouter « {input.trim()} »
            </button>
          )}
        </div>
      )}
    </div>
  );
}
